import type {
    GridDataSource,
    GridGetRowsParams,
    GridGetRowsResponse,
    GridRowModel,
    GridUpdateRowParams
} from "@mui/x-data-grid-premium";
import type {NestedRelatedDataModel} from "../../services/ApiHelpers.ts";
import type {BaseMuixApi} from "./BaseMuixApi.ts";
import type {BaseMuixFilterController} from "./controllers/BaseMuixFilterController.ts";
import type {BaseMuixTableController} from "./controllers/BaseMuixTableController.tsx";
import type {BaseMuixPageController} from "./controllers/BaseMuixPageController.ts";

export type PaginationGridRowModel = GridRowModel & {
    /**
     * Is true for the last row in a subgroup when there are more rows available
     * Used to display a subgroup pagination
     */
    subGroupPagination?: boolean
}

/**
 * DataModel as according to MuiX Data Grid specifications
 */
export const getBaseMuixDataGridSource: (
    databag: number, pageController: BaseMuixPageController,
    tableController: BaseMuixTableController, filterController: BaseMuixFilterController,
    reviewApi: BaseMuixApi, relatedDataFieldsSpec: NestedRelatedDataModel,
) => GridDataSource = (databag, pageController, tableController, filterController, reviewApi, relatedDataFieldsSpec) => {

    return {
        getRows: async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {
            // Grid is sending only groupKey of latest level,
            const groupKeys = tableController.getParentPath(params.groupKeys ?? []) as string[];
            // Store the related fields at time of query
            const relatedData = relatedDataFieldsSpec;
            // Page options would be set on reviewApi.get
            const response = await reviewApi.get(databag, {
                ...params,
                groupKeys,
            }, filterController.apiGridFilterModel, relatedData);
            pageController.onNoError();
            return {
                ...response.data,
                rows: tableController.transformGetRows(response.data.rows, response.data.rowCount, params.groupFields ?? [], groupKeys, relatedData)
            };
        },

        getGroupKey(row: GridRowModel): string {
            return tableController.dataSourceGetGroupKey(row);
        },

        getChildrenCount(row: GridRowModel): number {
            return row.count;
        },

        getAggregatedValue: function (row, field) {
            // console.debug('getTableDataModel.getAggregatedValue', row, field);
            // For last row in group by mode, we don't want aggregation to be displayed
            if (row.id === undefined || row.group === undefined)
                return '';
            return row[`${field}_agg`];
        },

        updateRow: async function (_params: GridUpdateRowParams) {
            // We do not use the edit function of MuiX Data Grid
            throw new Error('Not implemented');
        }

    } as const satisfies GridDataSource;
}
